import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { observer } from "mobx-react";
import { automationStore, emailStore, userStore, generalSettingStore, atsStore, candidateOwnerSettingStore, rappidStore } from "stores";
import stepAlertIcon from "assets/img/automation/step-alert.svg";
import SubmitButton from "./SubmitButton";
import { RappidNodeType, RappidText } from "helpers/Constants";
import { Enum } from "helpers";
import Select from 'react-select';
import { FormGroup, Input, Label, Row, Col, UncontrolledTooltip } from "reactstrap";
import HeaderForm from "./HeaderForm";
import { toJS } from "mobx";
import { EditRappidNode } from "components/Rappid/RappidCanvasFunctions";
import { EmailEditorButton, SidebarFormSelect } from "components/Automation/Styled";
import Alert from 'react-s-alert';
import { AutomationType, AutomationSpecificDays, TriggerType } from "helpers/enum";
import { AutomationLabelSchedule, AutomationDateSpan, DateFormGroup } from "../Styled";
import TimeInput from "./TimeInput";
import DatePicker from 'react-datepicker';
import { SwitchColor } from "_variable";
import Switch from "react-switch";
import moment from "moment";
import { getScheduleText, getRecordUpdate } from "helpers/AutomationHelper";
import CompactMergeTagsDropdown from "components/Automation/components/CompactMergeTagsDropdown";
import RichTextEditor from "components/RichTextEditor/RichTextEditor";
import SendTestEmailButton from "./SendTestEmailButton";
import { validateNodeAndResetBorder } from "components/Rappid/RappidCanvasFunctions";
import { SelectExternalUserEmail, AlertSectionTitle, AlertSummaryInfoCard, AlertSubjectWrapper, AlertCharCounter } from "./Styled";
import { isValidEmail } from "helpers/Validator";
import { SpecificUserSourceText, resolveLoggedInSpecificUser } from "./helpers/EmailSenderHelper";
import {
  buildAlertRecipientOptions,
  findSelectedAlertRecipients,
  alertRecipientState,
  defaultAlertRecipientState,
  countAlertRecipientUsers
} from "./helpers/AlertRecipientHelper";

const FREQUENCY_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const AlertForm = observer(forwardRef((props, ref) => {
  const { formAlert, automationAlertUserIds, automationAlertAtsUserIds, isCopiedTemplate, automationId, automationType, triggerType, automationState, companyDomainDefault } = automationStore;
  const { atsCategorisedFieldsForMergeTags, providerName } = atsStore;
  const { emailsList } = emailStore;
  const { candidateOwners } = candidateOwnerSettingStore;
  const alertEmailList = emailsList.filter(x => x.category && x.category.toLowerCase().includes("alert"));
  const isCandidatePlacementOrJobBasedTrigger = triggerType === TriggerType.PlacementBased || triggerType === TriggerType.JobBased;
  const subjectAlertRef = useRef();
  const editorRef = useRef(null);
  const sendTestEmailRef = useRef(null);
  const isDraft = automationState === Enum.AutomationStateType.Draft;
  const [isEditorLinkOpen, setIsEditorLinkOpen] = useState(false);
  const prevIncludeViewButtonRef = useRef(undefined);
  const alertFrequency = formAlert.alertFrequency || 'immediate';
  const isSummaryFrequency = alertFrequency !== 'immediate';

  const isScheduleMissing = () => {
    if (alertFrequency === 'immediate') {
      return formAlert.isSpecificTime && (!formAlert.startHour || !formAlert.specificDays?.length);
    }
    if (alertFrequency === 'monthly') {
      return !formAlert.startHour || !formAlert.alertMonthlyDay;
    }
    return !formAlert.startHour || !formAlert.specificDays?.length;
  };

  const isDisabledButton = (automationStore.isLegacyAlert && (!formAlert.emailCampaignId || !formAlert.name)) ||
    !formAlert.subject ||
    ((formAlert.userIds?.length || 0) === 0 && (formAlert.atsUserIds?.length || 0) === 0 && (formAlert.externalUserEmails?.length || 0) === 0 && !formAlert.isSendToOwner && !formAlert.isSendToPlacementOwner && !formAlert.isSendToPlacementRecruiter) ||
    isScheduleMissing() ||
    (!automationStore.isLegacyAlert && !formAlert.message);

  const isDisabledTest = !isDraft || !formAlert.subject || !formAlert.message;

  // ROI-372: the logged-in user as the recipient, read after the user lists load. Job-based and
  // placement-based triggers get no default.
  const getLoggedInRecipientState = () => {
    if (isCandidatePlacementOrJobBasedTrigger) return null;
    const options = buildAlertRecipientOptions(automationStore.automationAlertAtsUserIds, automationStore.automationAlertUserIds);
    return defaultAlertRecipientState(userStore.user, options);
  };

  useEffect(() => {
    const targetElement = rappidStore.activeElement;
    const initialFormAlert = { ...formAlert };
    const isEditingExistingAlert = targetElement?.attributes?.roiNodeType === RappidNodeType.Alert;
    // Read before the loads: an existing node is opened with isSidebarFormUpdate set (InitRappid).
    const isNewStep = !automationStore.isSidebarFormUpdate;
    Promise.all([emailStore.getEmails(), automationStore.getUsers(), atsStore.getCompanyInfo(), atsStore.getAtsField(), atsStore.getAtsCategorisedFields(automationType), automationStore.getCompanyDomainDefault(), candidateOwnerSettingStore.getCandidateOwners()]).then(() => {
      const isStaleNode = rappidStore.activeElement !== targetElement;
      const cloneFormAlert = isStaleNode ? { ...initialFormAlert } : { ...formAlert };
      if (isEditingExistingAlert && isCopiedTemplate === true && automationId !== null && typeof cloneFormAlert.isUpdatedFromCopiedTemplate === "undefined") {
        cloneFormAlert.emailTemplateId = null;
        cloneFormAlert.emailCampaignId = null;
        cloneFormAlert.name = "";
        cloneFormAlert.userIds = [];
        cloneFormAlert.atsUserIds = [];
        cloneFormAlert.externalUserEmails = [];
        // The template's recipients belong to someone else; start from the logged-in user, as a new step does.
        Object.assign(cloneFormAlert, getLoggedInRecipientState() ?? {});
        if (!isStaleNode) {
          automationStore.setFormAlert(cloneFormAlert, true)
          automationStore.setAutomationOnClear(true)
        }
        EditRappidNode(RappidNodeType.Alert, cloneFormAlert.name, "", "", "", cloneFormAlert, false, true, targetElement);
      }
      if (isStaleNode) return;
      //add new properties for handle old data alert and reset specific time #8293
      if (typeof formAlert.isSpecificTime === "undefined" || !automationStore.formAlert.isSpecificTime) {
        let formData = { ...automationStore.formAlert };
        formData.isSpecificTime = false;
        formData.specificDays = [AutomationSpecificDays.Monday, AutomationSpecificDays.Tuesday, AutomationSpecificDays.Wednesday, AutomationSpecificDays.Thursday, AutomationSpecificDays.Friday];
        formData.startHour = null;
        formData.convertedStartHour = null;
        if (!formData.alertFrequency) {
          formData.alertFrequency = 'immediate';
        }
        if (typeof formData.alertMonthlyDay === 'undefined') {
          formData.alertMonthlyDay = null;
        }
        automationStore.setFormAlert(formData, true);
      }
      //add new properties for handle old data alert (backward compatibility)
      if (typeof formAlert.atsUserIds === "undefined" || typeof formAlert.externalUserEmails === "undefined" ||
          typeof formAlert.message === "undefined" || typeof formAlert.isIncludeViewButton === "undefined" ||
          typeof formAlert.isSendToPlacementRecruiter === "undefined" || typeof formAlert.isBatchingSend === "undefined") {
        let formData = { ...automationStore.formAlert };
        if (typeof formData.atsUserIds === "undefined") formData.atsUserIds = [];
        if (typeof formData.externalUserEmails === "undefined") formData.externalUserEmails = [];
        if (typeof formData.message === "undefined") formData.message = "";
        if (typeof formData.isIncludeViewButton === "undefined") formData.isIncludeViewButton = false;
        if (typeof formData.isSendToPlacementRecruiter === "undefined") formData.isSendToPlacementRecruiter = false;
        if (typeof formData.isBatchingSend === "undefined") formData.isBatchingSend = false;
        automationStore.setFormAlert(formData, true);
      }
      // seed a default day for weekly/daily alerts loaded with an empty specificDays list
      // (e.g. older backend payloads), so the schedule validator never silently blocks submit
      const loaded = automationStore.formAlert;
      if (loaded.isSpecificTime && (!loaded.specificDays || loaded.specificDays.length === 0)) {
        automationStore.setFormAlert({
          ...loaded,
          specificDays: [AutomationSpecificDays.Monday],
        }, true);
      }
      // ROI-372: a new step starts with the logged-in user. Applied once, saved as a fixed id, and only
      // while no specific user is picked, so an existing step is never changed by opening it.
      const current = automationStore.formAlert;
      if (isNewStep && !current.userIds?.length && !current.atsUserIds?.length) {
        const defaultState = getLoggedInRecipientState();
        if (defaultState) automationStore.setFormAlert({ ...current, ...defaultState }, true);
      }
    });

    if (!isCandidatePlacementOrJobBasedTrigger) {
      automationStore.setFormAlert({
        ...automationStore.formAlert,
        isSendToOwner: formAlert.isSendToOwner,
        isSendToPlacementOwner: false,
        isSendToPlacementRecruiter: false
      }, true);
    } else {
      if (triggerType !== TriggerType.PlacementBased && formAlert.isSendToPlacementRecruiter) {
        automationStore.setFormAlert({
          ...automationStore.formAlert,
          isSendToPlacementOwner: true,
          isSendToPlacementRecruiter: false
        }, true);
      }
    }

    return function cleanup() {
      automationStore.resetFormAlert();
    }
    // eslint-disable-next-line
  }, []);

  console.debug("Alert Form Data: ", toJS(formAlert));

  // ROI-372: one "Specific user" list over userIds (Roi-AI) and atsUserIds (database), one entry per person.
  const specificUserOptions = buildAlertRecipientOptions(automationAlertAtsUserIds, automationAlertUserIds, formAlert);
  const selectedSpecificUsers = findSelectedAlertRecipients(formAlert, specificUserOptions);
  const loggedInSpecificUser = resolveLoggedInSpecificUser(userStore.user, specificUserOptions);

  const handleSpecificUsersChange = (selectedOptions) => {
    automationStore.setFormAlert({
      ...formAlert,
      ...alertRecipientState(selectedOptions, formAlert, automationAlertAtsUserIds, automationAlertUserIds)
    });
  };

  const formatSpecificUserOption = (option, { context }) => {
    const youTag = loggedInSpecificUser && option.key === loggedInSpecificUser.key ? " (you)" : "";
    if (context === "value") return `${option.fullName}${youTag}`;
    return (
      <div className="d-flex flex-column" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>{option.fullName}{youTag}</span>
        <span style={{ fontSize: '12px', opacity: 0.7 }}>{option.email} · {SpecificUserSourceText[option.kind]}</span>
      </div>
    );
  };

  const filterSpecificUserOption = (candidate, input) => {
    if (!input) return true;
    const query = input.trim().toLowerCase();
    return candidate.data.fullName.toLowerCase().includes(query) || candidate.data.email.toLowerCase().includes(query);
  };

  const selectedExternalUserEmails = formAlert.externalUserEmails
    ? formAlert.externalUserEmails.map(email => ({ value: email, label: email }))
    : [];

  const redirectToEmailEditor = (event) => {
    if (!formAlert.emailCampaignId)
      Alert.error('Select email first to continue')
    else
      window.open(`/admin/emails/editor/${formAlert.emailCampaignId}?fromAutomation=1`, '_blank', 'noopener,noreferrer')
  };

  const renderDaysComponent = (ListDays) => {
    let data = { ...automationStore.formAlert };
    data.specificDays = [...automationStore.formAlert.specificDays];
    data.timeVariableDays = data.specificDays.toString();

    const daysComponent = ListDays.map((item, index) => {
      let days = [...automationStore.formAlert.specificDays];
      let isChecked = days.find(x => x === item.value);

      return (
        <AutomationDateSpan key={index}>
          <Input
            type="checkbox"
            id={`text-form-time-variable-${item.day}`}
            className="automation-time-variable-checkbox-days text-white"
            name={`text-form-time-variable-${item.day}`}
            disabled={!isDraft}
            checked={isChecked ? true : false}
            onChange={(e) => {
              let data = { ...formAlert };
              data.specificDays = [...formAlert.specificDays];
              data.timeVariableDays = data.specificDays.slice();
              const checked = e.target.checked;
              if (checked) {
                data.specificDays = [...data.specificDays, item.value];
                data.timeVariableDays = data.specificDays.toString();
              } else {
                const removeValue = data.specificDays.indexOf(item.value);
                data.specificDays.splice(removeValue, 1);
                data.timeVariableDays = data.specificDays.toString();
              }
              automationStore.setFormAlert(data);
            }}
          />
          <Label htmlFor={`text-form-time-variable-${item.day}`} >
            {item.day}
          </Label>
        </AutomationDateSpan>
      );
    });

    return daysComponent;
  };

  const renderSingleDayComponent = (ListDays) => {
    const currentDay = formAlert.specificDays?.[0] || null;
    return ListDays.map((item, index) => (
      <AutomationDateSpan key={index}>
        <Input
          type="checkbox"
          id={`text-form-weekly-day-${item.day}`}
          className="automation-time-variable-checkbox-days text-white"
          name="weekly-day-select"
          disabled={!isDraft}
          checked={currentDay === item.value}
          onChange={() => automationStore.setFormAlert({ ...formAlert, specificDays: [item.value] })}
        />
        <Label htmlFor={`text-form-weekly-day-${item.day}`}>{item.day}</Label>
      </AutomationDateSpan>
    ));
  };

  const handleFrequencyChange = (frequency) => {
    let data = { ...formAlert, alertFrequency: frequency };
    if (frequency !== 'immediate') {
      data.isSpecificTime = true;
      if (!data.startHour) {
        data.startHour = new Date(moment().set({ hour: 8, minute: 0, second: 0, millisecond: 0 }));
        data.convertedStartHour = automationStore.convertStepTimeToString(data.startHour);
      }
      if (frequency === 'daily' && (!data.specificDays || data.specificDays.length === 0)) {
        data.specificDays = [AutomationSpecificDays.Monday];
      }
      if (frequency === 'weekly' && (!data.specificDays || data.specificDays.length !== 1)) {
        data.specificDays = data.specificDays?.length > 0 ? [data.specificDays[0]] : [AutomationSpecificDays.Monday];
      }
      if (frequency === 'monthly' && !data.alertMonthlyDay) {
        data.alertMonthlyDay = 1;
      }
      prevIncludeViewButtonRef.current = data.isIncludeViewButton;
      data.isIncludeViewButton = false;
    } else {
      data.isSpecificTime = false;
      data.startHour = null;
      data.convertedStartHour = null;
      data.specificDays = [AutomationSpecificDays.Monday, AutomationSpecificDays.Tuesday, AutomationSpecificDays.Wednesday, AutomationSpecificDays.Thursday, AutomationSpecificDays.Friday];
      if (typeof prevIncludeViewButtonRef.current !== 'undefined') {
        data.isIncludeViewButton = prevIncludeViewButtonRef.current;
        prevIncludeViewButtonRef.current = undefined;
      }
    }
    automationStore.setFormAlert(data);
  };

  const handleAutomationSubjectAlertTokenSelected = (token) => {
    const automationTextToken = `{{${token}}}`;

    if (subjectAlertRef.current) {
      subjectAlertRef.current.value = formAlert.subject + automationTextToken;
      automationStore.setFormAlert({ ...formAlert, subject: subjectAlertRef.current.value });
      console.log('ref: ', subjectAlertRef.current);
    }
  }

  const handleAlertMessageTokenSelected = (token) => {
    const messageToken = `{{${token}}}`;

    if (editorRef.current) {
      editorRef.current.insertContent(messageToken);
      automationStore.setFormAlert({ ...formAlert, message: editorRef.current.getContent() });
    }
  }

  useImperativeHandle(ref, () => ({
    isModalOpen: () => sendTestEmailRef.current?.isModalOpen() || isEditorLinkOpen || false,
    openModal: () => {
      sendTestEmailRef.current?.openModal();

      if (editorRef.current) {
        editorRef.current.execCommand('mceLink');
      }
    },
    closeModal: () => {
      sendTestEmailRef.current?.closeModal();

      if (editorRef.current && isEditorLinkOpen) {
        editorRef.current.windowManager.close();
      }
    },
  }));

  // #11667 allow insert external user emails
  const formatLabel = (option) => `Add ${option}`;

  const handleExternalUserEmailChanges = (option) => {
    automationStore.setFormAlert({ ...formAlert, externalUserEmails: option ? option.map(selected => selected.value) : [] });
  };

  const renderTimePicker = (idSuffix, showTimeLabel = false) => {
    const picker = (
      <DatePicker
        id={`text-form-input-${idSuffix}-hours`}
        autoComplete="off"
        disabled={!isDraft}
        selected={formAlert.convertedStartHour
          ? new Date(moment().set(automationStore.convertStringTimeToObject(formAlert.convertedStartHour)))
          : null}
        onChange={date => {
          automationStore.setFormAlert({ ...formAlert, startHour: date, convertedStartHour: automationStore.convertStepTimeToString(date) });
        }}
        className="w-100 font-12"
        showTimeSelect
        showTimeSelectOnly
        timeFormat="hh:mm aa"
        timeIntervals={60}
        dateFormat="h:mm aa"
        customInput={<TimeInput isSideBarForm={true} icon={true} />}
        placeholderText="Select time"
      />
    );
    if (showTimeLabel) return picker;
    return (
      <>
        <FormGroup row className="ml-3 mb-1 w-100">
          <Col md={12} lg={6} className="w-100 d-flex mt-2">
            <AutomationLabelSchedule className="text-white mt-2">at</AutomationLabelSchedule>
            {picker}
          </Col>
        </FormGroup>
        <FormGroup row className="ml-1 w-100">
          <p className="small ml-4 font-italic text-white">Your app's timezone is {generalSettingStore.timeZoneName}.</p>
        </FormGroup>
      </>
    );
  };

  return (
    <>
      <HeaderForm
        automationHeaderId="form-step-alert"
        automationHeaderImgSrc={stepAlertIcon}
        automationHeaderImgAlt="icon-alert"
        automationHeaderTextStep={RappidText.AlertStep}
      />

      <AlertSectionTitle>SEND TO</AlertSectionTitle>

      <FormGroup className="step-body-text">
        <Label className="text-white mb-1" for="automation-alert-specific-users-select">
          <span id="alertSpecificUserInfo" className="d-inline-flex align-items-center" style={{ cursor: 'help' }}>
            <b>Specific user</b>
            <i className="fa fa-info-circle ml-2" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}></i>
          </span>
        </Label>
        <UncontrolledTooltip placement="bottom" target="alertSpecificUserInfo">
          Choose the people who get this alert. New candidate and contact alerts start with you selected.
        </UncontrolledTooltip>
        <Select
          isMulti
          className="automation-alert-template automation-form-dropdown-multi"
          id="automation-alert-specific-users"
          inputId="automation-alert-specific-users-select"
          name="automation-alert-specific-users"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={specificUserOptions}
          isDisabled={!isDraft}
          onChange={handleSpecificUsersChange}
          getOptionLabel={(option) => option.fullName}
          getOptionValue={(option) => option.key}
          formatOptionLabel={formatSpecificUserOption}
          filterOption={filterSpecificUserOption}
          noOptionsMessage={() => "No users match your search."}
          value={selectedSpecificUsers}
        />
      </FormGroup>

      <FormGroup className="step-body-text">
        <Label className="text-white mb-1"><b>External email addresses</b></Label>
        <SelectExternalUserEmail
          id="automation-alert-external-users-emails"
          name="automation-alert-external-users-emails"
          className="automation-alert-template automation-form-dropdown-multi"
          classNamePrefix="automation-dropdown"
          placeholder="Enter email address"
          isMulti
          isDisabled={!isDraft}
          onChange={handleExternalUserEmailChanges}
          formatCreateLabel={formatLabel}
          isValidNewOption={(newOption) => isValidEmail(newOption)}
          value={selectedExternalUserEmails}
        />
      </FormGroup>

      <FormGroup className="step-body-text">
        <Input
          type="checkbox"
          id="automation-alert-default-owner"
          className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
          disabled={!isDraft}
          checked={formAlert.isSendToOwner}
          onChange={() => automationStore.setFormAlert({ ...formAlert, isSendToOwner: !formAlert.isSendToOwner })}
        />
        <Label check htmlFor="automation-alert-default-owner">
          {automationType === AutomationType.Client ? 'Contact owner(s)' : 'Recruiter(s)'}
        </Label>
      </FormGroup>

      {isCandidatePlacementOrJobBasedTrigger && (
        <FormGroup className="step-body-text">
          <Input
            type="checkbox"
            id="automation-alert-placement-owner"
            className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
            disabled={!isDraft}
            checked={formAlert.isSendToPlacementOwner}
            onChange={() => automationStore.setFormAlert({ ...formAlert, isSendToPlacementOwner: !formAlert.isSendToPlacementOwner })}
          />
          <Label check htmlFor="automation-alert-placement-owner">
            {automationType === AutomationType.Client ? 'Client job owner(s)' : 'Candidate job owner(s)'}
          </Label>
        </FormGroup>
      )}

      {triggerType === TriggerType.PlacementBased && atsStore.providerName !== Enum.AtsProvider.Bullhorn && (
        <FormGroup className="step-body-text">
          <Input
            type="checkbox"
            id="automation-alert-placement-recruiter"
            className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
            disabled={!isDraft}
            checked={formAlert.isSendToPlacementRecruiter}
            onChange={() => automationStore.setFormAlert({ ...formAlert, isSendToPlacementRecruiter: !formAlert.isSendToPlacementRecruiter })}
          />
          <Label check htmlFor="automation-alert-placement-recruiter">
            Placement recruiter(s)
          </Label>
        </FormGroup>
      )}

      <AlertSectionTitle marginTop="24px">SCHEDULING</AlertSectionTitle>

      <FormGroup className="step-body-text" style={{ marginBottom: '16px' }}>
        <Label className="text-white mb-1"><b>Frequency</b></Label>
        <Select
          classNamePrefix="automation-dropdown"
          placeholder="- Select frequency -"
          isDisabled={!isDraft}
          menuPosition="fixed"
          menuPlacement="auto"
          menuShouldBlockScroll={true}
          options={FREQUENCY_OPTIONS}
          getOptionLabel={(o) => o.label}
          getOptionValue={(o) => o.value}
          value={FREQUENCY_OPTIONS.find(o => o.value === alertFrequency) || null}
          onChange={e => handleFrequencyChange(e.value)}
        />
      </FormGroup>

      {isSummaryFrequency && (
        <AlertSummaryInfoCard>
          When using Daily, Weekly or Monthly Summaries the relevant profiles will automatically be added to the alert, including a link to view the related profile in your database.
        </AlertSummaryInfoCard>
      )}

      <FormGroup className="step-body-text" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                Email schedule
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                Set a specific time for this step to send.
              </div>
            </div>
            <Switch
              id="time-variable-switch"
              className="automation-sidebar-form-switch"
              uncheckedIcon={<div className="switch-specific-time">OFF</div>}
              checkedIcon={<div className="switch-specific-time">ON</div>}
              onChange={() => {
                if (isSummaryFrequency) return; // forced ON for summary modes
                let data = { ...formAlert };
                data.isSpecificTime = !formAlert.isSpecificTime;
                if (data.isSpecificTime) {
                  data.startHour = new Date(moment().set({ hour: 8, minute: 0, second: 0, millisecond: 0 }));
                  data.convertedStartHour = automationStore.convertStepTimeToString(data.startHour);
                } else {
                  data.startHour = null;
                  data.convertedStartHour = null;
                }
                automationStore.setFormAlert(data);
              }}
              disabled={!isDraft || isSummaryFrequency}
              checked={isSummaryFrequency ? true : !!formAlert.isSpecificTime}
              onColor={SwitchColor}
              onHandleColor="#EF4277"
              offHandleColor="#EF4277"
              height={23}
              width={42}
            />
          </div>

          {alertFrequency === 'immediate' && formAlert.isSpecificTime && (
            <FormGroup row className="mt-2 mb-0">
              <DateFormGroup row check>
                {renderDaysComponent(Enum.ListDaysShort)}
              </DateFormGroup>
              <FormGroup row className="ml-3 mb-1 w-100">
                <Col md={12} lg={6} className="w-100 d-flex mt-2">
                  <AutomationLabelSchedule className="text-white mt-2">at</AutomationLabelSchedule>
                  <DatePicker
                    id="text-form-input-start-hours"
                    autoComplete="off"
                    selected={new Date(moment().set(automationStore.convertStringTimeToObject(formAlert.convertedStartHour)))}
                    onChange={date => {
                      let data = { ...formAlert };
                      data.startHour = date;
                      data.convertedStartHour = automationStore.convertStepTimeToString(date);
                      automationStore.setFormAlert(data);
                    }}
                    className="w-100 font-12"
                    showTimeSelect
                    showTimeSelectOnly
                    timeFormat="hh:mm aa"
                    timeIntervals={60}
                    dateFormat="h:mm aa"
                    customInput={<TimeInput isSideBarForm={true} icon={true} />}
                  />
                </Col>
              </FormGroup>
              <FormGroup row className="ml-1 w-100">
                <p className="small ml-4 font-italic text-white">Your app's timezone is {generalSettingStore.timeZoneName}.</p>
              </FormGroup>
            </FormGroup>
          )}

          {alertFrequency === 'daily' && (
            <FormGroup row className="mt-2 mb-0">
              <DateFormGroup row check>
                {renderDaysComponent(Enum.ListDaysShort)}
              </DateFormGroup>
              {renderTimePicker('daily')}
            </FormGroup>
          )}

          {alertFrequency === 'weekly' && (
            <FormGroup row className="mt-2 mb-0">
              <DateFormGroup row check>
                {renderSingleDayComponent(Enum.ListDaysShort)}
              </DateFormGroup>
              {renderTimePicker('weekly')}
            </FormGroup>
          )}

          {alertFrequency === 'monthly' && (
            <div className="mt-2">
              <Row className="ml-0 mr-0">
                <Col xs={7} className="pl-0 pr-1">
                  <Label className="text-white mb-1" style={{ fontSize: '12px' }}>Date</Label>
                  <Select
                    classNamePrefix="automation-dropdown"
                    placeholder="- Select date -"
                    isDisabled={!isDraft}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldBlockScroll={true}
                    options={Array.from({ length: 31 }, (_, i) => {
                      const n = i + 1;
                      const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
                      return { value: n, label: `${n}${suffix} of every month` };
                    })}
                    value={formAlert.alertMonthlyDay
                      ? (() => {
                          const n = formAlert.alertMonthlyDay;
                          const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
                          return { value: n, label: `${n}${suffix} of every month` };
                        })()
                      : null}
                    onChange={e => automationStore.setFormAlert({ ...formAlert, alertMonthlyDay: e.value })}
                  />
                </Col>
                <Col xs={5} className="pl-1 pr-0">
                  <Label className="text-white mb-1" style={{ fontSize: '12px' }}>Time</Label>
                  {renderTimePicker('monthly', true)}
                </Col>
              </Row>
              <p className="small mt-2 font-italic text-white">Your app's timezone is {generalSettingStore.timeZoneName}.</p>
            </div>
          )}
        </FormGroup>

        <AlertSectionTitle>ALERT CONTENT</AlertSectionTitle>

        <FormGroup className="step-body-text">
          {automationStore.isLegacyAlert ? (
            <Row className="ml-0 mr-0">
              <SidebarFormSelect
                className="automation-alert-template col-md-12 px-0 mb-2"
                id="automation-alert-email-template"
                name="automation-alert-email-template"
                classNamePrefix="automation-dropdown"
                placeholder="- Select email -"
                isDisabled={!isDraft}
                onChange={e => {
                  const emailCampaignId = parseInt(e.id);
                  const emailCampaignName = e.name;
                  automationStore.setFormAlert({ ...formAlert, emailCampaignId, name: emailCampaignName });
                }}
                options={alertEmailList}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={alertEmailList.find(x => x.id === formAlert.emailCampaignId) || 0}
              />
              <EmailEditorButton
                id="automation-alert-form-redirect-editor-button"
                className="col-md-6 col-lg-4 mt-3 btn-roi secondary outline-white my-3 pr-5"
                disabled={!formAlert.emailCampaignId}
                onClick={redirectToEmailEditor}>
                View Alert
              </EmailEditorButton>
              <Label className="ml-1">
                To view your Alert templates here, please ensure they are categorised as 'Alert'
              </Label>
            </Row>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong><Label className="text-white mb-0">Message</Label></strong>
                {!isSummaryFrequency && (
                  <CompactMergeTagsDropdown
                    atsCategorisedFieldsForMergeTags={atsCategorisedFieldsForMergeTags}
                    onTagSelected={(tokenVariable) => handleAlertMessageTokenSelected(tokenVariable)}
                    isDisabled={!isDraft}
                    isAutomationContact={automationType !== AutomationType.Candidate && triggerType === TriggerType.PlacementBased}
                    buttonText="Insert merge tag"
                  />
                )}
              </div>
              <RichTextEditor
                onInit={(evt, editor) => {
                  editorRef.current = editor;
                  editorRef.current.on('OpenWindow', function(e) {
                    if (e.dialog) setIsEditorLinkOpen(true);
                  });
                  editorRef.current.on('CloseWindow', function(e) {
                    if (e.dialog) setIsEditorLinkOpen(false);
                  });
                }}
                name="automation-alert-form-message"
                id="automation-alert-form-message"
                initialValue={formAlert.message}
                disabled={!isDraft}
                handleEditorChange={e => {
                  automationStore.setFormAlert({ ...formAlert, message: e });
                }}
                restInit={{
                  toolbar: 'bold italic underline alignleft alignright aligncenter alignjustify | link unlink',
                  height: "250",
                  placeholder: "Type Here...",
                  content_style:
                    "@import url('https://fonts.googleapis.com/css?family=Calibri:400,700,400italic,700italic');" +
                    "@import url('https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap')"
                }}
              />
              {!isSummaryFrequency && (
                <Row className="mt-2">
                  <Col sm={12} md={12}>
                    <Input
                      type="checkbox"
                      id="automation-alert-include-view-button"
                      className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
                      disabled={!isDraft}
                      checked={formAlert.isIncludeViewButton}
                      onChange={() => automationStore.setFormAlert({ ...formAlert, isIncludeViewButton: !formAlert.isIncludeViewButton })}
                    />
                    <Label check htmlFor="automation-alert-include-view-button">
                      Include button <strong>View {automationType === AutomationType.Client ? 'Contact' : 'Candidate'} in Database</strong>
                    </Label>
                  </Col>
                </Row>
              )}
            </>
          )}
        </FormGroup>

        <FormGroup className="step-body-text" style={{ marginBottom: '15px' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong><Label className="text-white mb-0">Subject</Label></strong>
            {!isSummaryFrequency && (
              <CompactMergeTagsDropdown
                atsCategorisedFieldsForMergeTags={atsCategorisedFieldsForMergeTags}
                onTagSelected={(tokenVariable) => handleAutomationSubjectAlertTokenSelected(tokenVariable)}
                isDisabled={!isDraft}
                isAutomationContact={automationType !== AutomationType.Candidate && triggerType === TriggerType.PlacementBased}
                buttonText="Insert merge tag"
              />
            )}
          </div>
          <AlertSubjectWrapper>
            <Input
              ref={subjectAlertRef}
              type="textarea"
              rows='2'
              id="automation-alert-subject"
              className="automation-alert-input border-radius-normal"
              disabled={!isDraft}
              maxLength={256}
              value={formAlert.subject}
              onChange={e => {
                const value = e.target.value;
                if (value.length <= 256) {
                  automationStore.setFormAlert({ ...formAlert, subject: value });
                }
              }}
              style={{ paddingBottom: '24px' }}
            />
            <AlertCharCounter
              className={`text-muted ${(formAlert.subject?.length || 0) > 240 ? 'text-warning' : ''} ${(formAlert.subject?.length || 0) >= 256 ? 'text-danger' : ''}`}
            >
              {formAlert.subject?.length || 0}/256
            </AlertCharCounter>
          </AlertSubjectWrapper>
        </FormGroup>

        {!automationStore.isLegacyAlert && (
          <>
            <AlertSectionTitle marginTop="24px">TESTING</AlertSectionTitle>
            <div style={{ marginBottom: '24px' }}>
              <SendTestEmailButton
                ref={sendTestEmailRef}
                formEmail={formAlert}
                candidateOwners={candidateOwners}
                contactType={automationType}
                companyDomain={companyDomainDefault}
                isDisabled={isDisabledTest}
                isAlert={true}
              />
            </div>
          </>
        )}

      {isDraft && (
        <SubmitButton
          type={RappidNodeType.Alert}
          text={renderAlertNodeLine1(formAlert, automationType, triggerType)}
          text2={renderAlertNodeLine2(formAlert)}
          text3={renderAlertNodeLine3(formAlert)}
          text4={renderAlertNodeLine4(formAlert)}
          roiData={formAlert}
          validateConditions={() => validateNodeAndResetBorder()}
          isDisabled={isDisabledButton} />
      )}
    </>
  );
}));

// The owner checkboxes mean different people by campaign (ROI-412): the owner is the contact owner on
// a contact campaign, and the job owner checkbox sends to the placement owner on a placement campaign.
// The campaign's types are passed in rather than read from automationStore, which the report canvas
// never sets. Without them the line reads as a candidate campaign with a job owner.
export const renderAlertNodeLine1 = (formAlert, automationType, triggerType) => {
  const recipients = [];
  if (formAlert?.isSendToOwner)
    recipients.push(automationType === AutomationType.Client ? "Contact owner" : "Recruiter");
  if (formAlert?.isSendToPlacementOwner)
    recipients.push(triggerType === TriggerType.PlacementBased ? "Placement owner" : "Job owner");
  if (formAlert?.isSendToPlacementRecruiter)
    recipients.push("Placement recruiter");
  // ROI-372: Roi-AI and database users are one "Specific user" list, counted as one number.
  const specificUserCount = countAlertRecipientUsers(formAlert);
  if (specificUserCount > 0)
    recipients.push(specificUserCount + " user(s)");
  if (formAlert?.externalUserEmails && formAlert?.externalUserEmails?.length > 0)
    recipients.push(formAlert?.externalUserEmails?.length + " external email(s)");
  return "To: " + recipients.join(" & ");
}

export const renderAlertNodeLine2 = (formAlert) => {
  return "Alert Email: " + formAlert?.name;
}

export const renderAlertNodeLine3 = (formAlert) => {
  if (formAlert?.isSpecificTime) {
    return getScheduleText(formAlert.specificDays, formAlert.convertedStartHour ?? formAlert.startHour);
  }
  else if (formAlert?.isUpdateRecord)
    return getRecordUpdate(formAlert?.updateRecordType, automationStore.automationType);
  return '';
}

// not sure form alert is ever update record, think this is redundant and should be removed
export const renderAlertNodeLine4 = (formAlert) => {
  if (formAlert.isSpecificTime && formAlert?.isUpdateRecord)
    return getRecordUpdate(formAlert?.updateRecordType, automationStore.automationType);
  return '';
}

export default AlertForm;
